/*
  AI Auto-Scheduler modal. Renders a list of proposed schedule slots for
  unscheduled tasks. User can accept per-task or commit all.

  Hidden behind the `feature_auto_scheduler` admin flag.
*/
import { useState } from "react";
import { Sparkles, CalendarClock, Loader2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { phase2Api } from "../lib/phase2Api";
import { trackEvent } from "../lib/analytics";

export default function AutoSchedulerModal({ open, onClose, onCommitted }) {
  const [loading, setLoading] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [horizon, setHorizon] = useState(7);
  const [skipped, setSkipped] = useState(0);
  const [accepted, setAccepted] = useState({}); // task_id -> bool

  async function propose() {
    setLoading(true);
    try {
      const res = await phase2Api.proposeSchedule({ horizon_days: horizon });
      setProposals(res.proposals || []);
      setSkipped(res.skipped || 0);
      setAccepted(Object.fromEntries((res.proposals || []).map(p => [p.task_id, true])));
      trackEvent("auto_scheduler_proposed", { count: res.proposals?.length || 0 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't propose");
    } finally {
      setLoading(false);
    }
  }

  async function commit() {
    const chosen = proposals.filter(p => accepted[p.task_id]);
    if (chosen.length === 0) { toast.error("Nothing selected"); return; }
    setLoading(true);
    try {
      const { committed } = await phase2Api.commitSchedule(chosen);
      trackEvent("auto_scheduler_committed", { count: committed });
      toast.success(`Scheduled ${committed} task${committed === 1 ? "" : "s"}`);
      onCommitted?.();
      close();
    } catch (err) {
      toast.error("Commit failed");
    } finally { setLoading(false); }
  }

  function close() {
    setProposals([]);
    setAccepted({});
    setSkipped(0);
    onClose();
  }

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="auto-scheduler-modal">
      <div className="absolute inset-0 bg-black/50" onClick={close} />
      <div className="relative w-full sm:max-w-lg sm:rounded-2xl rounded-t-3xl p-5 pb-[max(20px,env(safe-area-inset-bottom))] max-h-[90vh] overflow-y-auto"
        style={{ background: "var(--surface)", boxShadow: "0 -20px 40px -10px rgba(0,0,0,0.25)" }}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-lg bg-[color:var(--primary-soft)] flex items-center justify-center shrink-0">
              <Sparkles className="h-4 w-4 text-[color:var(--primary-ink)]" />
            </div>
            <div>
              <div className="label-caps">AI Auto-Scheduler</div>
              <h3 className="font-display font-bold text-xl tracking-tight">Find me time.</h3>
              <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>Fills empty slots over the next few days for undated tasks.</p>
            </div>
          </div>
          <button onClick={close} className="p-2 rounded hover:bg-[color:var(--surface-2)]" data-testid="auto-sched-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {proposals.length === 0 && (
          <div className="space-y-3">
            <div>
              <label className="label-caps">Horizon (days)</label>
              <input type="range" min={1} max={14} value={horizon} onChange={(e) => setHorizon(Number(e.target.value))}
                data-testid="sched-horizon"
                className="w-full mt-2 accent-[color:var(--primary)]" />
              <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{horizon} day{horizon === 1 ? "" : "s"}</div>
            </div>
            <button onClick={propose} disabled={loading} data-testid="sched-propose"
              className="w-full h-11 rounded-lg font-semibold text-sm text-white inline-flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ background: "var(--primary)" }}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {loading ? "Planning…" : "Propose schedule"}
            </button>
          </div>
        )}

        {proposals.length > 0 && (
          <>
            <div className="mb-3 text-xs" style={{ color: "var(--text-muted)" }}>
              {proposals.length} task{proposals.length === 1 ? "" : "s"} proposed{skipped ? ` · ${skipped} skipped` : ""}.
            </div>
            <ul className="space-y-2" data-testid="proposal-list">
              {proposals.map(p => {
                const when = p.scheduled_for ? new Date(p.scheduled_for).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : null;
                return (
                  <li key={p.task_id} className="flex items-start gap-3 p-3 rounded-lg border" style={{ borderColor: "var(--border-subtle)" }}>
                    <input type="checkbox" checked={!!accepted[p.task_id]}
                      onChange={(e) => setAccepted(a => ({ ...a, [p.task_id]: e.target.checked }))}
                      data-testid={`accept-${p.task_id}`}
                      className="mt-1 accent-[color:var(--primary)]" />
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm truncate">{p.title}</div>
                      <div className="text-xs mt-0.5 inline-flex items-center gap-1" style={{ color: "var(--text-muted)" }}>
                        <CalendarClock className="h-3 w-3" /> {when} · {p.duration_minutes} min
                        {p.priority && <span> · {p.priority}</span>}
                      </div>
                      {p.reasoning && <div className="text-[11px] italic mt-1" style={{ color: "var(--text-muted)" }}>{p.reasoning}</div>}
                    </div>
                  </li>
                );
              })}
            </ul>
            <div className="mt-4 flex gap-2">
              <button onClick={commit} disabled={loading} data-testid="sched-commit"
                className="flex-1 h-11 rounded-lg font-semibold text-sm text-white inline-flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ background: "var(--primary)" }}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Apply selected
              </button>
              <button onClick={() => { setProposals([]); setAccepted({}); }}
                className="h-11 px-4 rounded-lg font-semibold text-sm border hover:bg-[color:var(--surface-2)]"
                style={{ borderColor: "var(--border-subtle)" }}>
                Redo
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
